/**
 * Permission Type Service
 * 
 * HTTP service for managing permission types in the admin panel.
 * Provides CRUD operations for permission type management.
 */

import BaseHttpService from "./https.service";

const isLocal = window.location.href.indexOf('localhost') >= 0;
const managerBaseUrl = isLocal ? 'http://localhost:5001/manager' : '/manager';

class PermissionTypeService extends BaseHttpService {
    constructor() {
        super(managerBaseUrl);
    }

    /**
     * Get all permission types
     * @returns {Promise<Object>} Response containing all permission types
     */
    async getAllPermissionTypes() {
        return await this.get("/permission-types/all");
    }

    /**
     * Get all unique categories
     * @returns {Promise<Object>} Response containing all categories
     */
    async getCategories() {
        return await this.get("/permission-types/categories");
    }

    /**
     * Get a specific permission type by ID
     * @param {string} id - Permission type ID
     * @returns {Promise<Object>} Response containing the permission type
     */
    async getPermissionType(id) {
        return await this.get("/permission-types/item", { id });
    }

    /**
     * Add a new permission type
     * @param {Object} data - Permission type data
     * @param {string} data.key - Permission key (e.g., "retention.view")
     * @param {string} data.name - Display name
     * @param {string} data.category - Category name
     * @param {string} data.description - Description
     * @param {Object} [data.metadata] - Optional metadata
     * @returns {Promise<Object>} Response containing the new permission type ID
     */
    async addPermissionType(data) {
        return await this.post("/permission-types/add", data);
    }

    /**
     * Edit an existing permission type
     * @param {string} id - Permission type ID
     * @param {Object} data - Updated permission type data
     * @returns {Promise<Object>} Response confirming the update
     */
    async editPermissionType(id, data) {
        return await this.post("/permission-types/edit", { id, data });
    }

    /**
     * Delete a permission type
     * @param {string} id - Permission type ID
     * @returns {Promise<Object>} Response confirming deletion
     */
    async deletePermissionType(id) {
        return await this.post("/permission-types/delete", { id });
    }

    /**
     * Get cache status
     * @returns {Promise<Object>} Response containing cache status
     */
    async getCacheStatus() {
        return await this.get("/permission-types/cache/status");
    }

    /**
     * Refresh permission types cache
     * @returns {Promise<Object>} Response confirming cache refresh
     */
    async refreshCache() {
        return await this.post("/permission-types/cache/refresh", {});
    }
}

export const permissionTypeService = new PermissionTypeService();
export default PermissionTypeService;

